import React, { useState, useEffect } from 'react'

// Selected against remaining lessons. Over the balance, a purchase link (when the
// caller gives one) shows the way out instead of only a disabled button.
export function BalanceCounter({ selectedCount, remainingLessons, buyMoreUrl, buyMoreLabel, t = {} }) {
  const overBalance = selectedCount > remainingLessons
  return (
    <div className={`text-sm mb-3 ${overBalance ? 'text-red-600' : 'text-gray-500'}`}>
      {(t.balance_counter || 'Selected %{selected}, %{remaining} remaining')
        .replace('%{selected}', selectedCount)
        .replace('%{remaining}', remainingLessons)}
      {overBalance && (
        <span className="ml-1">
          ({t.over_balance || 'exceeds balance'})
        </span>
      )}
      {overBalance && buyMoreUrl && buyMoreLabel && (
        <a href={buyMoreUrl} className="ml-2 font-medium text-blue-600 underline hover:text-blue-800">{buyMoreLabel}</a>
      )}
    </div>
  )
}

export default function RecurringBookingPreview({
  courseId,
  teacherId,
  selectedSlot,
  remainingLessons,
  timezone,
  onSubmit,
  submitting = false,
  skipScheduleCheck = false,
  buyMoreUrl = null,
  buyMoreLabel = null,
  translations: t = {}
}) {
  const [repeatWeeks, setRepeatWeeks] = useState(1)
  const [previewSlots, setPreviewSlots] = useState([])
  const [checkedSlots, setCheckedSlots] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Reset when slot changes
  useEffect(() => {
    setRepeatWeeks(1)
    setPreviewSlots([])
    setCheckedSlots({})
    setError(null)
  }, [selectedSlot?.start_time])

  // Fetch preview when repeatWeeks changes to 2+
  useEffect(() => {
    if (repeatWeeks < 2 || !selectedSlot) return
    fetchPreview()
  }, [repeatWeeks])

  const fetchPreview = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`/api/team_courses/${courseId}/bookings/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          teacher_id: teacherId,
          start_time: selectedSlot.start_time,
          repeat_weeks: repeatWeeks,
          skip_schedule_check: skipScheduleCheck ? 'true' : undefined
        })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Preview failed')

      setPreviewSlots(data.slots)
      // Auto-check available slots
      const checked = {}
      data.slots.forEach((slot, i) => {
        if (slot.available) checked[i] = true
      })
      setCheckedSlots(checked)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleSlot = (index) => {
    if (!previewSlots[index]?.available) return
    setCheckedSlots(prev => {
      const next = { ...prev }
      if (next[index]) {
        delete next[index]
      } else {
        next[index] = true
      }
      return next
    })
  }

  const selectedCount = repeatWeeks < 2 ? 1 : Object.keys(checkedSlots).length
  const overBalance = selectedCount > remainingLessons

  const handleSubmit = () => {
    if (repeatWeeks < 2) {
      // Single booking - delegate to parent's existing handler
      onSubmit(null)
      return
    }

    const selectedSlots = previewSlots
      .filter((_, i) => checkedSlots[i])
      .map(slot => ({ start_time: slot.start_time }))

    onSubmit(selectedSlots)
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('zh-TW', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      timeZone: timezone || undefined
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone || undefined
    })
  }

  const reasonLabel = (reason) => {
    const labels = {
      conflict: t.reason_conflict || 'Already booked',
      teacher_unavailable: t.reason_unavailable || 'Teacher unavailable',
      past: t.reason_past || 'Past time'
    }
    return labels[reason] || reason
  }

  return (
    <div className="mt-3">
      {/* Repeat weeks dropdown */}
      <div className="flex items-center gap-2 mb-3">
        <label className="text-sm text-gray-600 whitespace-nowrap">
          {t.repeat_label || 'Repeat weekly'}
        </label>
        <select
          value={repeatWeeks}
          onChange={(e) => setRepeatWeeks(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
            <option key={n} value={n}>
              {n === 1 ? (t.single_lesson || 'Single') : `${n} ${t.weeks_label || 'weeks'}`}
            </option>
          ))}
        </select>
      </div>

      {/* Preview list */}
      {repeatWeeks >= 2 && (
        <>
          {loading ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              {t.loading_preview || 'Checking availability...'}
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-3">
              {error}
            </div>
          ) : previewSlots.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-y-auto max-h-[240px] lg:max-h-[280px] mb-3">
              {previewSlots.map((slot, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm ${
                    index > 0 ? 'border-t border-gray-100' : ''
                  } ${!slot.available ? 'bg-gray-50 text-gray-400' : ''}`}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={!!checkedSlots[index]}
                    disabled={!slot.available}
                    onChange={() => toggleSlot(index)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />

                  {/* Date & time */}
                  <span className="flex-1">
                    {formatDate(slot.start_time)}
                  </span>
                  <span className="text-gray-500">
                    {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                  </span>

                  {/* Status */}
                  {slot.available ? (
                    <span className="text-green-500 text-xs">✓</span>
                  ) : (
                    <span className="text-red-400 text-xs" title={reasonLabel(slot.reason)}>
                      ✗ {reasonLabel(slot.reason)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Balance counter */}
          {previewSlots.length > 0 && !loading && (
            <BalanceCounter
              selectedCount={selectedCount}
              remainingLessons={remainingLessons}
              buyMoreUrl={buyMoreUrl}
              buyMoreLabel={buyMoreLabel}
              t={t}
            />
          )}
        </>
      )}

      {/* Submit button */}
      <button
        onClick={handleSubmit}
        disabled={!selectedSlot || submitting || overBalance || (repeatWeeks >= 2 && selectedCount === 0)}
        className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors cursor-pointer"
      >
        {submitting
          ? (t.booking_in_progress || 'Booking...')
          : repeatWeeks >= 2
            ? (t.confirm_batch || 'Confirm %{count} Bookings').replace('%{count}', selectedCount)
            : selectedSlot
              ? (t.confirm_booking || 'Confirm Booking')
              : (t.select_time_slot || 'Select a time slot')}
      </button>
    </div>
  )
}
